import type {
  BestCommentListQueryVariables,
  BlockUserListQueryVariables,
  BuyCashHistoryQueryVariables,
  BuyTicketHistoryQueryVariables,
  CommentItemQueryVariables,
  CommentListQueryVariables,
  ContentTicketDetailQueryVariables,
  ContentTicketInfoQueryVariables,
  MyCommentListQueryVariables,
  MyNewsQueryVariables,
  MyRatingQueryVariables,
  PopupQueryVariables,
  ReceiptInfoQueryVariables,
  ReceiptInfoResultQueryVariables,
  ReplyCommentListQueryVariables,
  SinglePreviewQueryVariables,
  TodayGiftListQueryVariables,
  TvPointAgreementQueryVariables,
  UseCashHistoryQueryVariables,
  UserCashQueryVariables,
  UseTicketHistoryQueryVariables,
  WelcomeQueryVariables,
} from '../../graphql/generated/types';
import {ADAPTER_QUERY_KEY_PREFIX} from '../constant';

/**
 * Store 도메인 Query Key 관리
 */
export const STORE_QUERY_KEY_MAP = {
  /**
   * 베스트 댓글 목록 조회 (Infinite)
   * Origin: useInfiniteBestCommentListQuery.getKey
   */
  infiniteBestCommentList: (variables: BestCommentListQueryVariables) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'bestCommentList.infinite',
    variables,
  ],
  /**
   * 캐시 충전 내역 조회 (Infinite)
   * Origin: useInfiniteBuyCashHistoryQuery.getKey
   */
  infiniteBuyCashHistory: (variables: BuyCashHistoryQueryVariables) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'buyCashHistory.infinite',
    variables,
  ],
  /**
   * 이용권 충전 내역 조회 (Infinite)
   * Origin: useInfiniteBuyTicketHistoryQuery.getKey
   */
  infiniteBuyTicketHistory: (variables?: BuyTicketHistoryQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'buyTicketHistory.infinite'] as const)
      : ([
          ADAPTER_QUERY_KEY_PREFIX,
          'buyTicketHistory.infinite',
          variables,
        ] as const),
  /**
   * 차단 사용자 목록 조회
   * Origin: useBlockUserListQuery.getKey
   */
  blockUserList: (variables?: BlockUserListQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'blockUserList'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'blockUserList', variables] as const),
  /**
   * 댓글 단일 항목 조회
   * Origin: useCommentItemQuery.getKey
   */
  commentItem: (variables: CommentItemQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'commentItem', variables] as const,
  /**
   * 댓글 목록 조회 (Infinite)
   * Origin: useInfiniteCommentListQuery.getKey
   */
  infiniteCommentList: (variables: CommentListQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'commentList.infinite', variables] as const,
  /**
   * 이용권 상세 정보 조회
   * Origin: useContentTicketDetailQuery.getKey
   */
  contentTicketDetail: (variables: ContentTicketDetailQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'contentTicketDetail', variables] as const,
  /**
   * 회차 조회
   * Origin: useGetSingleQuery.getKey
   */
  singlePreview: (variables: SinglePreviewQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'singlePreview', variables] as const,
  /**
   * 현금영수증 발행정보 조회
   * Origin: useReceiptInfoResultQuery.getKey
   */
  receiptInfoResult: (variables: ReceiptInfoResultQueryVariables) => [
    ADAPTER_QUERY_KEY_PREFIX,
    'receiptInfoResult',
    variables,
  ],
  /**
   * 콘텐츠 이용권 정보 조회 (Infinite)
   * Origin: useInfiniteContentTicketInfoQuery.getKey
   */
  infiniteContentTicketInfo: (variables: ContentTicketInfoQueryVariables) =>
    [
      ADAPTER_QUERY_KEY_PREFIX,
      'contentTicketInfo.infinite',
      variables,
    ] as const,
  /**
   * 내 댓글 목록 조회 (Infinite)
   * Origin: useInfiniteMyCommentListQuery.getKey: ['myCommentList.infinite', variables]
   */
  infiniteMyCommentList: (variables: MyCommentListQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'myCommentList.infinite', variables] as const,
  /**
   * 답글 목록 조회 (Infinite)
   * Origin: useInfiniteReplyCommentListQuery.getKey: ['replyCommentList.infinite', variables]
   */
  infiniteReplyCommentList: (variables: ReplyCommentListQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'replyCommentList.infinite', variables] as const,
  /**
   * 캐시 사용 내역 조회 (Infinite)
   * Origin: useInfiniteUseCashHistoryQuery.getKey
   */
  infiniteUseCashHistory: (variables: UseCashHistoryQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'useCashHistory.infinite', variables] as const,
  /**
   * 이용권 사용 내역 조회 (Infinite)
   * Origin: useInfiniteUseTicketHistoryQuery.getKey
   */
  infiniteUseTicketHistory: (variables?: UseTicketHistoryQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'useTicketHistory.infinite'] as const)
      : ([
          ADAPTER_QUERY_KEY_PREFIX,
          'useTicketHistory.infinite',
          variables,
        ] as const),
  /**
   * 오늘의 선물 목록 조회
   * Origin: useTodayGiftListQuery.getKey
   */
  todayGiftList: (variables?: TodayGiftListQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'todayGiftList'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'todayGiftList', variables] as const),
  /**
   * 사용자 캐시 정보 조회
   * Origin: useUserCashQuery.getKey
   */
  userCash: (variables?: UserCashQueryVariables) =>
    variables === undefined
      ? [ADAPTER_QUERY_KEY_PREFIX, 'userCash']
      : [ADAPTER_QUERY_KEY_PREFIX, 'userCash', variables],
  /**
   * 웰컴 팝업 정보 조회
   * Origin: useWelcomeQuery.getKey
   */
  welcome: (variables?: WelcomeQueryVariables) =>
    variables === undefined
      ? [ADAPTER_QUERY_KEY_PREFIX, 'Welcome']
      : [ADAPTER_QUERY_KEY_PREFIX, 'Welcome', variables],
  /**
   * 내 소식 목록 조회
   * Origin: useMyNewsQuery.getKey
   */
  myNews: (variables: MyNewsQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'myNews', variables] as const,
  /**
   * 내 평점 조회
   * Origin: useMyRatingQuery.getKey
   */
  myRating: (variables: MyRatingQueryVariables) =>
    [ADAPTER_QUERY_KEY_PREFIX, 'myRating', variables] as const,
  /**
   * 팝업 정보 조회
   * Origin: usePopupQuery.getKey
   */
  popup: (variables?: PopupQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'Popup'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'Popup', variables] as const),
  /**
   * 현금 영수증 정보 조회
   * Origin: useReceiptInfoQuery.getKey
   */
  receiptInfo: (variables?: ReceiptInfoQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'receiptInfo'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'receiptInfo', variables] as const),
  /**
   * TVPoint 약관 동의 여부 조회
   * Origin: useTvPointAgreementQuery.getKey
   */
  tvPointAgreement: (variables?: TvPointAgreementQueryVariables) =>
    variables === undefined
      ? ([ADAPTER_QUERY_KEY_PREFIX, 'tvPointAgreement'] as const)
      : ([ADAPTER_QUERY_KEY_PREFIX, 'tvPointAgreement', variables] as const),
} as const;

/**
 * Store 도메인 Mutation Key 관리
 */
export const STORE_MUTATION_KEY_MAP = {
  /**
   * 사용자 차단
   * Origin: useBlockUserMutation (getKey 없음)
   */
  blockUser: 'blockUser',
  /**
   * 댓글 신고
   * Origin: useDeclareCommentMutation (getKey 없음)
   */
  declareComment: 'declareComment',
  /**
   * 시리즈 알림 설정
   * Origin: useSetSeriesAlarmMutation (getKey 없음)
   */
  setSeriesAlarm: 'setSeriesAlarm',
  /**
   * 시리즈 좋아요 설정
   * Origin: useSetSeriesLikeMutation (getKey 없음)
   */
  setSeriesLike: 'setSeriesLike',
  /**
   * 영수증 정보 삭제
   * Origin: useDeleteReceiptInfoMutation (getKey 없음)
   */
  deleteReceiptInfo: 'deleteReceiptInfo',
  /**
   * 영수증 정보 변경
   * Origin: useUpdateReceiptInfoMutation (getKey 없음)
   */
  updateReceiptInfo: 'updateReceiptInfo',
  /**
   * 닉네임 변경
   * Origin: useUpdateNicknameMutation (getKey 없음)
   */
  updateNickname: 'updateNickname',
  /**
   * 리딤 코드 사용
   * Origin: useUseRedeemMutation (getKey 없음)
   */
  useRedeem: 'useRedeem',
  /**
   * 댓글 작성
   * Origin: useWriteCommentMutation (getKey 없음)
   */
  writeComment: 'writeComment',
  /**
   * 내 평점 설정
   * Origin: useSetMyRatingMutation (getKey 없음)
   */
  setMyRating: 'setMyRating',
  /**
   * 댓글 좋아요/좋아요 취소
   * Origin: useLikeCommentMutation (getKey 없음)
   */
  likeComment: 'likeComment',
} as const;
